import { useEffect, useState, useCallback } from "react";
import { api, apiError, timeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, Clock, WarningCircle, CheckCircle, UserPlus, Archive, ArrowCounterClockwise,
} from "@phosphor-icons/react";import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { isAdmin as isAdminRole, isSuper, INTERNAL_ROLES, ROLE_LABELS } from "@/lib/roles";
import { ArchiveDialog, PermanentDeleteDialog, lifecycleError } from "@/components/LifecycleDialogs";

const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const blankStaff = { name: "", email: "", password: "", role: "teacher", phone: "", room: "", rooms: [], all_rooms: false,
                     title: "", hire_date: "", hourly_wage: 0, certifications: "", emergency_contact: "" };
const blankShift = { staff_email: "", room: "", date: "", weekday: 0, start_time: "08:00",
                     end_time: "16:00", recurring: false, note: "" };

const today = () => new Date().toISOString().slice(0, 10);
const weekAgo = () => new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);

export default function Staff() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(["team", "shifts", "timecards", "archives"].includes(params.get("view")) ? params.get("view") : "team");
  const isAdmin = isAdminRole(user);
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [cards, setCards] = useState([]);
  const [ratios, setRatios] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [mine, setMine] = useState(null);
  const [range, setRange] = useState({ start: params.get("day") || weekAgo(), end: params.get("day") || today() });
  const [sOpen, setSOpen] = useState(false);
  const [shOpen, setShOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blankStaff);
  const [sh, setSh] = useState(blankShift);
  const [archives, setArchives] = useState([]);
  const [archiving, setArchiving] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const loadArchives = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/staff/archives");
      setArchives(data);
    } catch (e) { toast.error(lifecycleError(e.response?.data?.detail)); }
  }, [isAdmin]);

  const load = useCallback(async () => {
    const [st, sf, tc, ra, me, rm] = await Promise.all([
      api.get("/staff"),
      api.get("/shifts", { params: { start: range.start, end: range.end } }),
      api.get("/timecards", { params: { start: range.start, end: range.end } }),
      api.get("/ratios"),
      api.get("/timecards/me"),
      api.get("/rooms"),
    ]);
    setStaff(st.data); setShifts(sf.data); setCards(tc.data); setRatios(ra.data); setMine(me.data); setRooms(rm.data.map((r) => r.room));
    await loadArchives();
  }, [range, loadArchives]);

  useEffect(() => { load(); }, [load]);

  const clock = async (kind) => {
    try {
      await api.post("/timecards/clock", { kind, room: user?.room || "" });
      toast.success(kind === "in" ? "Clocked in — have a great shift!" : "Clocked out. Thank you!");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveStaff = async () => {
    try {
      const payload = { ...f, hourly_wage: parseFloat(f.hourly_wage) || 0 };
      if (!payload.password) delete payload.password;
      if (editing) await api.put(`/staff/${editing}`, payload);
      else {
        const { data } = await api.post("/staff", payload);
        if (data.invited) toast.success(`Invitation ${data.invitation?.send?.result === "sent" ? "emailed to" : "created for"} ${payload.email}`);
        else toast.success("Staff member added");
        setSOpen(false); load(); return;
      }
      toast.success("Staff member updated");
      setSOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const restoreStaff = async (s) => {
    try {
      const { data } = await api.post(`/staff/${s.id}/restore`, { reason: "Restored from the staff archive" });
      toast.success(data.message || "Staff member restored");
      load();
    } catch (e) { toast.error(lifecycleError(e.response?.data?.detail)); }
  };

  const saveShift = async () => {
    try {
      const payload = { ...sh, weekday: sh.recurring ? Number(sh.weekday) : null };
      await api.post("/shifts", payload);
      toast.success("Shift scheduled");
      setShOpen(false);
      setSh(blankShift);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const delShift = async (id) => {
    try { await api.delete(`/shifts/${id}`); toast.success("Shift removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const delCard = async (id) => {
    try { await api.delete(`/timecards/${id}`); toast.success("Timecard deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const hoursByStaff = cards.reduce((a, c) => {
    a[c.staff_name || c.staff_email] = Math.round(((a[c.staff_name || c.staff_email] || 0) + Number(c.hours || 0)) * 100) / 100;
    return a;
  }, {});

  return (
    <div data-testid="staff-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Team</p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Staff & schedules</h1>
        </div>
        {isAdmin && (
          <div className="flex gap-3 flex-wrap">
            <Button variant="outline" onClick={() => { setSh(blankShift); setShOpen(true); }}
                    className="rounded-full h-12 px-6 font-bold" data-testid="add-shift-btn">Add shift</Button>
            <Button onClick={() => { setEditing(null); setF(blankStaff); setSOpen(true); }}
                    className="rounded-full h-12 px-6 bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="add-staff-btn"><UserPlus size={18} className="mr-2" /> Add staff</Button>
          </div>
        )}
      </div>

      {/* Clock in / out */}
      <div className="mt-8 bg-[#0F172A] text-white rounded-3xl p-8 flex flex-wrap gap-6 items-center"
           data-testid="clock-card">
        <span className="h-14 w-14 rounded-2xl bg-white/10 grid place-items-center shrink-0">
          <Clock size={28} weight="duotone" className="text-[#FBBF24]" />
        </span>
        <div className="flex-1 min-w-[12rem]">
          <p className="font-display text-2xl font-bold">
            {mine?.open ? "You're on the clock" : "You're clocked out"}
          </p>
          <p className="mt-1 text-sm text-white/60" data-testid="clock-status">
            {mine?.open
              ? `Clocked in at ${timeOf(mine.open.clock_in)}${mine.open.room ? ` · ${mine.open.room}` : ""}`
              : "Tap clock in when you start your shift."}
            {mine ? ` · ${mine.week_hours}h this week` : ""}
          </p>
        </div>
        {mine?.open ? (
          <Button onClick={() => clock("out")}
                  className="h-12 px-8 rounded-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-bold"
                  data-testid="clock-out-btn">Clock out</Button>
        ) : (
          <Button onClick={() => clock("in")}
                  className="h-12 px-8 rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold"
                  data-testid="clock-in-btn">Clock in</Button>
        )}
      </div>

      {/* Live ratios */}
      <h2 className="mt-10 font-display text-2xl font-semibold">Live ratios right now</h2>
      <div className="mt-4 grid sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="ratios-list">
        {ratios.map((r) => (
          <div key={r.room} className={`bg-white rounded-2xl border-2 p-6 ${
            !r.ratio_known ? "border-[#FBBF24]" : r.compliant ? "border-border" : "border-[#EF4444]"}`}
               data-testid={`ratio-card-${r.room}`}>
            <div className="flex justify-between items-start gap-2">
              <p className="font-bold">{r.room}</p>
              {!r.ratio_known ? (
                <WarningCircle size={22} weight="fill" className="text-[#FBBF24] shrink-0"
                               data-testid={`ratio-setup-${r.room}`} />
              ) : r.compliant ? (
                <CheckCircle size={22} weight="fill" className="text-[#10B981] shrink-0" />
              ) : (
                <WarningCircle size={22} weight="fill" className="text-[#EF4444] shrink-0"
                               data-testid={`ratio-alert-${r.room}`} />
              )}
            </div>
            <p className="mt-4 font-display text-3xl font-bold" data-testid={`ratio-value-${r.room}`}>
              {r.children_present} : {r.staff_present}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">
              children : staff on the clock
            </p>
            {r.ratio_known ? (
              <p className="mt-4 text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">
                Limit 1:{r.max_ratio} · needs {r.staff_needed} staff
              </p>
            ) : (
              <p className="mt-4 text-xs font-bold text-amber-700" data-testid={`ratio-unknown-${r.room}`}>
                {r.review_room ? "Temporary placement room — not used for attendance or scheduling"
                               : "Ratio unknown — setup required"}
              </p>
            )}
            {!r.compliant && r.ratio_known && (
              <p className="mt-2 text-sm font-bold text-[#EF4444]">
                Out of ratio — {r.staff_needed - r.staff_present} more staff needed
              </p>
            )}
            {r.over_capacity && (
              <p className="mt-1 text-sm font-bold text-[#EF4444]">Over room capacity ({r.capacity})</p>
            )}
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="rounded-full">
          <TabsTrigger value="team" className="rounded-full font-bold" data-testid="tab-team">Team</TabsTrigger>
          <TabsTrigger value="shifts" className="rounded-full font-bold" data-testid="tab-shifts">Shifts</TabsTrigger>
          <TabsTrigger value="timecards" className="rounded-full font-bold" data-testid="tab-timecards">Timecards</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="archives" className="rounded-full font-bold" data-testid="tab-archives">
              Archives{archives.length ? ` (${archives.length})` : ""}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="team" className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="staff-list">
          {staff.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-border p-6" data-testid={`staff-card-${s.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <p className="font-display text-lg font-semibold truncate">{s.name}</p>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1">
                    {s.title || s.role}
                  </p>
                </div>
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-muted shrink-0">{s.role}</span>
              </div>
              <dl className="mt-5 space-y-2 text-sm">
                {[["Email", s.email], ["Phone", s.phone || "—"], ["Room", s.room || "—"],
                  ["Hired", s.hire_date || "—"], ["Wage", s.hourly_wage ? `$${s.hourly_wage}/hr` : "—"],
                  ["Certifications", s.certifications || "—"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <dt className="text-muted-foreground font-semibold">{k}</dt>
                    <dd className="font-semibold text-right truncate">{v}</dd>
                  </div>
                ))}
              </dl>
              {isAdmin && (
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline"
                          onClick={() => { setEditing(s.id); setF({ ...blankStaff, ...s, password: "" }); setSOpen(true); }}
                          className="rounded-full font-bold" data-testid={`edit-staff-${s.id}`}>
                    <PencilSimple size={15} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setArchiving(s)}
                          className="rounded-full font-bold text-amber-700" data-testid={`archive-staff-${s.id}`}>
                    <Archive size={15} className="mr-1" /> Archive
                  </Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="archives" className="mt-6" data-testid="staff-archives">
          <p className="text-sm text-muted-foreground max-w-2xl">
            Archived staff are out of every active list, ratio count and future shift. Payroll, tax, timecard,
            classroom and audit history is kept, so most records can never be permanently deleted — and the
            reason is shown when you try.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {archives.length === 0 && (
              <p className="text-muted-foreground" data-testid="archives-empty">Nobody is archived.</p>
            )}
            {archives.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl border border-dashed border-slate-300 p-6"
                   data-testid={`archived-staff-${s.id}`}>
                <p className="font-display text-lg font-semibold truncate">{s.immutable_label || s.name}</p>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mt-1">
                  {ROLE_LABELS[s.role] || s.role}
                </p>
                <dl className="mt-5 space-y-2 text-sm">
                  {[["Email", s.email], ["Former room", s.former_room || "—"],
                    ["Archived", (s.archived_at || "").slice(0, 10) || "—"], ["By", s.archived_by || "—"],
                    ["Reason", s.archive_reason || "—"],
                    ["Timecards kept", String(s.dependencies?.timecards ?? 0)],
                    ["Audit entries", String(s.dependencies?.audit_entries ?? 0)]].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3">
                      <dt className="text-muted-foreground font-semibold">{k}</dt>
                      <dd className="font-semibold text-right truncate">{v}</dd>
                    </div>
                  ))}
                </dl>
                {!s.eligible_for_permanent_deletion && (
                  <p className="mt-4 text-xs font-semibold text-amber-700" data-testid={`retained-${s.id}`}>
                    Records must be retained, so this account cannot be permanently deleted.
                  </p>
                )}
                {isAdmin && !s.deleted && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => restoreStaff(s)}
                            className="rounded-full font-bold text-emerald-700" data-testid={`restore-staff-${s.id}`}>
                      <ArrowCounterClockwise size={15} className="mr-1" /> Restore
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDeleting(s)}
                            className="rounded-full font-bold text-[#EF4444]" data-testid={`delete-staff-${s.id}`}>
                      <Trash size={15} className="mr-1" /> Delete
                    </Button>
                  </div>
                )}
                {s.deleted && (
                  <p className="mt-5 text-xs font-bold text-[#EF4444]" data-testid={`deleted-${s.id}`}>
                    Permanently deleted — sign-in revoked, history kept.
                  </p>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="shifts" className="mt-6 space-y-3" data-testid="shifts-list">
          {shifts.length === 0 && <p className="text-muted-foreground">No shifts scheduled in this range.</p>}
          {shifts.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                 data-testid={`shift-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-bold">{s.staff_name || s.staff_email}</p>
                <p className="text-xs text-muted-foreground mt-1 font-semibold">
                  {s.recurring ? `Every ${DOW[s.weekday ?? 0]}` : s.date} · {s.start_time}–{s.end_time}
                  {s.room ? ` · ${s.room}` : ""}
                </p>
              </div>
              {s.recurring && (
                <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                  weekly
                </span>
              )}
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => delShift(s.id)}
                        className="rounded-full font-bold text-[#EF4444]" data-testid={`delete-shift-${s.id}`}>
                  <Trash size={15} />
                </Button>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="timecards" className="mt-6" data-testid="timecards-list">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-sm font-bold">From</Label>
              <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })}
                     className="mt-2 h-11 rounded-xl w-44" data-testid="timecard-start" />
            </div>
            <div>
              <Label className="text-sm font-bold">To</Label>
              <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })}
                     className="mt-2 h-11 rounded-xl w-44" data-testid="timecard-end" />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3" data-testid="hours-totals">
            {Object.entries(hoursByStaff).map(([name, h]) => (
              <span key={name} className="bg-white border border-border rounded-2xl px-5 py-3">
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">{name}</span>
                <span className="ml-3 font-display text-lg font-bold">{h}h</span>
                {h > 40 && <span className="ml-2 text-xs font-bold text-[#EF4444]">OT</span>}
              </span>
            ))}
          </div>

          <div className="mt-5 space-y-3">
            {cards.length === 0 && <p className="text-muted-foreground">No timecards in this range.</p>}
            {cards.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                   data-testid={`timecard-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold">{c.staff_name || c.staff_email}</p>
                  <p className="text-xs text-muted-foreground mt-1 font-semibold">
                    {c.date} · {timeOf(c.clock_in)} → {c.clock_out ? timeOf(c.clock_out) : "still on the clock"}
                    {c.room ? ` · ${c.room}` : ""}{c.edited_by ? ` · edited by ${c.edited_by}` : ""}
                  </p>
                </div>
                <p className="font-display text-xl font-bold">{c.hours || 0}h</p>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => delCard(c.id)}
                          className="rounded-full font-bold text-[#EF4444]" data-testid={`delete-timecard-${c.id}`}>
                    <Trash size={15} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Staff dialog */}
      <ArchiveDialog open={Boolean(archiving)} onOpenChange={(o) => !o && setArchiving(null)}
                     kind="staff" record={archiving} onDone={load} />
      <PermanentDeleteDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}
                             kind="staff" record={deleting} onDone={load} />

      <Dialog open={sOpen} onOpenChange={setSOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit staff member" : "Add staff member"}</DialogTitle>
            <DialogDescription className="sr-only">Staff contact, role and employment details.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["name", "Full name"], ["email", "Email", "email"], ["phone", "Phone"],
              ["title", "Job title"], ["hire_date", "Hire date", "date"],
              ["hourly_wage", "Hourly wage", "number"], ["certifications", "Certifications"],
              ["emergency_contact", "Emergency contact"]].map(([k, label, type]) => (
              <div key={k}>
                <Label className="text-sm font-bold">{label}</Label>
                <Input type={type || "text"} value={f[k] ?? ""} disabled={editing && k === "email"}
                       onChange={(e) => setF({ ...f, [k]: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid={`staff-field-${k}`} />
              </div>
            ))}
            <div>
              <Label className="text-sm font-bold">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="staff-field-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INTERNAL_ROLES.filter((r) => r !== "super_admin" || isSuper(user)).map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold">Assigned room</Label>
              <Select value={f.room || "none"} onValueChange={(v) => setF({ ...f, room: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="staff-field-room"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No room</SelectItem>
                  {rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-bold">Classrooms this teacher may access</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="staff-rooms">
                {rooms.map((r) => (
                  <label key={r} className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border">
                    <input type="checkbox" checked={(f.rooms || []).includes(r)}
                           onChange={(e) => setF({ ...f, rooms: e.target.checked ? [...(f.rooms || []), r] : (f.rooms || []).filter((x) => x !== r) })}
                           data-testid={`staff-room-${r}`} /> {r}
                  </label>
                ))}
                <label className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#3B82F6] text-[#3B82F6]">
                  <input type="checkbox" checked={Boolean(f.all_rooms)} onChange={(e) => setF({ ...f, all_rooms: e.target.checked })} data-testid="staff-all-rooms" /> All classrooms
                </label>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Teachers see only children in these classrooms. Leave empty for unrestricted access.</p>
            </div>
            <div className="sm:col-span-2">
              {editing ? (
                <>
                  <Label className="text-sm font-bold">New password (leave blank to keep)</Label>
                  <Input type="password" value={f.password ?? ""} onChange={(e) => setF({ ...f, password: e.target.value })}
                         className="mt-2 h-11 rounded-xl" data-testid="staff-field-password" />
                </>
              ) : (
                <div className="rounded-2xl bg-blue-50 p-4 text-sm" data-testid="staff-invite-note">
                  <p className="font-bold">An invitation email will be sent.</p>
                  <p className="mt-1 text-muted-foreground">They open the secure one-time link, choose Google or create their own password, and are signed in with the role you picked. Nobody chooses their own role.</p>
                  {isSuper(user) && (
                    <details className="mt-3">
                      <summary className="text-xs font-bold cursor-pointer">Set a temporary password instead (super administrator only)</summary>
                      <Input type="password" value={f.password ?? ""} onChange={(e) => setF({ ...f, password: e.target.value })}
                             placeholder="10+ characters, upper, lower, number" className="mt-2 h-11 rounded-xl bg-white" data-testid="staff-field-password" />
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
          <Button onClick={saveStaff} className="mt-4 w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="staff-save-btn">{editing ? "Save changes" : f.password ? "Create account" : "Send invitation"}</Button>
        </DialogContent>
      </Dialog>

      {/* Shift dialog */}
      <Dialog open={shOpen} onOpenChange={setShOpen}>
        <DialogContent className="rounded-3xl" data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Schedule a shift</DialogTitle>
            <DialogDescription className="sr-only">Assign a staff member to a room and time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold">Staff member</Label>
              <Select value={sh.staff_email} onValueChange={(v) => setSh({ ...sh, staff_email: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="shift-staff">
                  <SelectValue placeholder="Choose a staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => <SelectItem key={s.email} value={s.email}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold">Room</Label>
              <Select value={sh.room || "none"} onValueChange={(v) => setSh({ ...sh, room: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="shift-room"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any room</SelectItem>
                  {rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-bold">Classrooms this teacher may access</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="staff-rooms">
                {rooms.map((r) => (
                  <label key={r} className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border">
                    <input type="checkbox" checked={(f.rooms || []).includes(r)}
                           onChange={(e) => setF({ ...f, rooms: e.target.checked ? [...(f.rooms || []), r] : (f.rooms || []).filter((x) => x !== r) })}
                           data-testid={`staff-room-${r}`} /> {r}
                  </label>
                ))}
                <label className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#3B82F6] text-[#3B82F6]">
                  <input type="checkbox" checked={Boolean(f.all_rooms)} onChange={(e) => setF({ ...f, all_rooms: e.target.checked })} data-testid="staff-all-rooms" /> All classrooms
                </label>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Teachers see only children in these classrooms. Leave empty for unrestricted access.</p>
            </div>
            <label className="flex items-center gap-3 text-sm font-semibold">
              <input type="checkbox" checked={sh.recurring}
                     onChange={(e) => setSh({ ...sh, recurring: e.target.checked })}
                     data-testid="shift-recurring" /> Repeats weekly
            </label>
            {sh.recurring ? (
              <div>
                <Label className="text-sm font-bold">Weekday</Label>
                <Select value={String(sh.weekday)} onValueChange={(v) => setSh({ ...sh, weekday: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="shift-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOW.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-sm font-bold">Date</Label>
                <Input type="date" value={sh.date} onChange={(e) => setSh({ ...sh, date: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="shift-date" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold">Start</Label>
                <Input type="time" value={sh.start_time} onChange={(e) => setSh({ ...sh, start_time: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="shift-start" />
              </div>
              <div>
                <Label className="text-sm font-bold">End</Label>
                <Input type="time" value={sh.end_time} onChange={(e) => setSh({ ...sh, end_time: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="shift-end" />
              </div>
            </div>
            <Button onClick={saveShift} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="shift-save-btn"><Plus size={18} className="mr-2" /> Schedule shift</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
